import Page from 'components/Page';
import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import ActionsList from 'components/common/ActionsList';
import Button from 'components/common/buttons/Button';
import Grid from 'components/common/Grid';
import { getFontFaceFromStyle } from 'support/textUtils';
import { translate } from 'support/translate';
import EmptyListSection from './EmptyListSection';
import { SelectableTileType } from 'components/common/tiles/SelectableTile';
import { Config } from 'types/api/config';
import { reportItemSelected } from 'services/analytics/reportingServicePage';
import { Media } from 'types/api/media';
import { SelectItemContext } from 'types/events';
import { TileParentContext } from 'components/common/tiles/Tile';
import TextLabel from 'components/common/TextLabel';

const CONTENT_X = 80;
const CONTENT_Y = 120;
const CONTENT_WIDTH = 1760;
const CONTENT_HEIGHT = 1170;

const EDIT_CONTAINER_HEIGHT = 68;
const EDIT_CONTAINER_WIDTH = 1760;

const EDIT_ACTIONS_LIST_SPACING = 20;
const EDIT_BUTTON_WIDTH = 128;
const EDIT_BUTTON_HEIGHT = 60;

const SELECT_ALL_BUTTON_WIDTH = 149;
const SELECT_ALL_BUTTON_HEIGHT = 60;

const REMOVE_BUTTON_WIDTH = 172;
const REMOVE_BUTTON_HEIGHT = 60;

const TITLE_FONT_SIZE = 36;
const TITLE_HEIGHT = 42;
const TITLE_BASELINE = TITLE_HEIGHT - 9;

const SUBTITLE_FONT_SIZE = 24;
const SUBTITLE_HEIGHT = 28;
const SUBTITLE_BASELINE = SUBTITLE_HEIGHT - 6;

const PADDING_VERTICAL = 30;
const ITEMS_LIST_PADDING = 6;

const EMPTY_SECTION_HEIGHT = 900;

const EDIT_ICONS = {
  focused: 'static/images/edit-icon-active.svg',
  unfocused: 'static/images/edit-icon-inactive.svg',
};

const CANCEL_ICONS = {
  focused: 'static/images/close-icon-active.svg',
  unfocused: 'static/images/close-icon-inactive.svg',
};

const TRASH_ICONS = {
  focused: 'static/images/trash-icon-active.svg',
  unfocused: 'static/images/trash-icon-inactive.svg',
  clicked: 'static/images/trash-icon-disabled.svg',
};

export interface AbstractDetailsPageTemplateSpec
  extends Lightning.Component.TemplateSpec {
  items?: unknown[];

  Content: {
    EditContainer: {
      EditButton: typeof Button;
      EditActionsList: typeof ActionsList;
    };
    Title: typeof TextLabel;
    Subtitle: typeof TextLabel;
    ItemsList: typeof Grid;
    EmptyGridSection: {
      EmptySection: typeof EmptyListSection;
    };
  };
}

export default abstract class AbstractDetailsPage<
  TemplateSpec extends AbstractDetailsPageTemplateSpec = AbstractDetailsPageTemplateSpec,
> extends Page<TemplateSpec> {
  private _Content = (this as any).getByRef('Content');
  private _EditContainer = this._Content.getByRef('EditContainer')!;
  private _EditButton = this._EditContainer.getByRef('EditButton')!;
  private _EditActionsList = this._EditContainer.getByRef('EditActionsList')!;
  private _SelectAllButton = this._EditActionsList.getItemByRef(
    'SelectAllButton',
  )! as Button;
  private _RemoveButton = this._EditActionsList.getItemByRef(
    'RemoveButton',
  )! as Button;

  protected _EmptyGridSection = this._Content.getByRef('EmptyGridSection')!;
  protected _EmptySection = this._EmptyGridSection.getByRef('EmptySection')!;

  protected _ItemsList = this._Content.getByRef('ItemsList')! as Grid;
  private _Title = this._Content.getByRef('Title')!;
  private _Subtitle = this._Content.getByRef('Subtitle')!;
  private _isEditing = false;
  private _isAllSelected = false;

  protected loginConfig: Config['login']['logged_in'] | undefined = undefined;

  protected _items: unknown[] = [];

  protected $remove() {
    this._setState('RemoveConfirmationModal');
  }

  protected $removeItems() {
    this._setState('EditActionsList');
  }

  protected $dismissModal() {
    this._setState('EditActionsList');
  }

  get announce() {
    // override in child
    return '';
  }

  get items(): unknown[] {
    return this._items;
  }

  set items(items: unknown[]) {
    this._items = items;
  }

  set title(title: string) {
    this._Title.patch({
      visible: !!title,
      text: {
        text: title,
      },
    });
  }

  set subtitle(text: string) {
    this._Subtitle.patch({
      visible: !!text,
      text: { text },
    });
  }

  set showEditButton(showEditButton: boolean) {
    // Show/hide entire edit container to account for spacing
    this._EditContainer.patch({ visible: showEditButton });
    this._EditButton.patch({ visible: showEditButton });
  }

  set showEmptyGridSection(showEmptyGridSection: boolean) {
    showEmptyGridSection
      ? this.setupEmptyGridSection()
      : this.hideEmptyGridSection();
  }

  static override _template(): Lightning.Component.Template<AbstractDetailsPageTemplateSpec> {
    return {
      ...super._template(),
      Content: {
        x: CONTENT_X,
        y: CONTENT_Y,
        w: CONTENT_WIDTH,
        h: CONTENT_HEIGHT,
        flex: {
          direction: 'column',
        },
        EmptyGridSection: {
          w: CONTENT_WIDTH,
          h: EMPTY_SECTION_HEIGHT,
          rect: true,
          visible: false,
          color: Colors('alternateBackground').alpha(0.65).get(),
          EmptySection: {
            w: CONTENT_WIDTH,
            h: EMPTY_SECTION_HEIGHT,
            visible: false,
            rect: true,
            color: Colors('alternateBackground').alpha(0.65).get(),
            type: EmptyListSection,
            signals: {
              $onHover: '$onHover',
            },
          },
        },
        EditContainer: {
          h: EDIT_CONTAINER_HEIGHT,
          w: EDIT_CONTAINER_WIDTH,
          visible: false,
          flex: {
            direction: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
          EditButton: {
            type: Button,
            minWidth: EDIT_BUTTON_WIDTH,
            height: EDIT_BUTTON_HEIGHT,
            label: translate('global.editButton'),
            action: '$toggleEdit',
            signals: {
              $toggleEdit: true,
              $onHover: true,
            },
            startIcon: EDIT_ICONS,
          },
          EditActionsList: {
            type: ActionsList,
            visible: false,
            flex: {
              direction: 'row',
            },
            signals: {
              $onHover: true,
              $selectAll: true,
              $remove: true,
            },
            passSignals: {
              $onHover: true,
              $selectAll: true,
              $remove: true,
            },
            spacing: EDIT_ACTIONS_LIST_SPACING,
            items: [
              {
                type: Button,
                minWidth: SELECT_ALL_BUTTON_WIDTH,
                height: SELECT_ALL_BUTTON_HEIGHT,
                label: translate('myStuff.selectAll'),
                ref: 'SelectAllButton',
                action: '$selectAll',
                signals: {
                  $onHover: true,
                },
                passSignals: {
                  $selectAll: true,
                },
              },
              {
                type: Button,
                minWidth: REMOVE_BUTTON_WIDTH,
                height: REMOVE_BUTTON_HEIGHT,
                label: translate('global.remove'),
                ref: 'RemoveButton',
                action: '$remove',
                startIcon: TRASH_ICONS,
                signals: {
                  $onHover: true,
                },
                passSignals: {
                  $remove: true,
                },
              },
            ],
          },
        },
        Title: {
          type: TextLabel,
          flexItem: {
            marginTop: PADDING_VERTICAL,
            marginBottom: PADDING_VERTICAL,
          },
          text: {
            h: TITLE_HEIGHT,
            offsetY: TITLE_BASELINE,
            fontSize: TITLE_FONT_SIZE,
            fontFace: getFontFaceFromStyle('regular'),
          },
        },
        Subtitle: {
          type: TextLabel,
          visible: false,
          flexItem: {
            marginBottom: PADDING_VERTICAL,
          },
          text: {
            h: SUBTITLE_HEIGHT,
            offsetY: SUBTITLE_BASELINE,
            fontSize: SUBTITLE_FONT_SIZE,
            fontFace: getFontFaceFromStyle('regular'),
          },
        },
        ItemsList: {
          flex: {
            padding: ITEMS_LIST_PADDING,
          },
          type: Grid,
          w: (w: number) => w,
          h: (h: number) => h,
          columns: 6, // Default 6, override in child
          signals: {
            $onHover: true,
          },
          clipping: true,
          forceLoad: true,
        },
      },
    };
  }

  override _setup() {
    super._setup();
    this._setState('EditButton');
  }

  override _disable() {
    if (this._isEditing) this.$toggleEdit();
  }

  override _getFocused() {
    return this._EditButton;
  }

  override _active() {
    super._active();
    this.loginConfig = AppData?.config?.login.logged_in;
  }

  protected setupRemoveConfirmationModal() {
    this.widgets.removeconfirmationmodal.update({
      onRemoveItems: this.$removeItems.bind(this),
      onDismissModal: this.$dismissModal.bind(this),
    });
  }

  private $onHover(target: Lightning.Component) {
    switch (target) {
      case this._EditActionsList:
        this._setState('EditActionsList');
        break;
      case this._EditButton:
        this._setState('EditButton');
        break;
      case this._ItemsList:
        this._setState('ItemsList');
        break;
      case this._EmptySection.button:
        this._setState('EmptyGridSection');
        break;
    }
  }

  private setupEmptyGridSection() {
    this.showEditButton = false;

    this._Title.patch({
      visible: false,
    });

    this._Subtitle.patch({
      visible: false,
    });

    this._EditContainer.patch({
      visible: false,
    });

    this._EmptyGridSection.patch({
      visible: true,
    });

    this._EmptySection.patch({
      visible: true,
    });

    this._setState('EmptyGridSection');
  }

  private updateRemoveButton(color: number, fontColor: number, icon: string) {
    this._RemoveButton?.patch({
      color,
      backgroundColor: {
        unfocused: color,
      },
      fontColor: {
        unfocused: fontColor,
      },
    });
    this._RemoveButton.setStartIcon(icon);
  }

  private hideEmptyGridSection() {
    this._EmptyGridSection.patch({
      visible: false,
    });

    this._EmptySection.patch({
      visible: false,
    });
    this._setState('EditButton');
  }

  protected $toggleEdit() {
    this._isEditing = !this._isEditing;
    this._EditButton.patch({
      startIcon: {
        focused: this._isEditing ? CANCEL_ICONS.focused : EDIT_ICONS.focused,
        unfocused: this._isEditing
          ? CANCEL_ICONS.unfocused
          : EDIT_ICONS.unfocused,
      },
      label: this._isEditing
        ? translate('global.cancelButton')
        : translate('global.editButton'),
    });

    // Reset select all button
    this._isAllSelected = false;
    this._SelectAllButton.patch({
      label: this._isAllSelected
        ? translate('myStuff.deselectAll')
        : translate('myStuff.selectAll'),
    });

    this._EditActionsList.patch({ visible: this._isEditing });
    this.showSelectionMode(this._isEditing);
    this.disableRemoveButton();
  }

  private $selectAll() {
    this._isAllSelected = !this._isAllSelected;
    this._ItemsList.items.forEach(item => {
      (item as SelectableTileType).isSelected = this._isAllSelected;
    });

    this._SelectAllButton.patch({
      label: this._isAllSelected
        ? translate('myStuff.deselectAll')
        : translate('myStuff.selectAll'),
    });
    this._isAllSelected
      ? this.enableRemoveButton()
      : this.disableRemoveButton();
  }

  override $onTileSelected(
    data?: Media,
    context: SelectItemContext<TileParentContext> = {},
  ) {
    this.reportItemSelected(this._pageId, data, context);
    super.$onTileSelected(data, context);
  }

  private $onTileEditSelected() {
    // Check if all items are selected / deselected
    const allSelected = this._ItemsList.items.every(
      item => (item as SelectableTileType).isSelected,
    );

    const shouldActivateRemoveButton = this._ItemsList.items.some(
      item => (item as SelectableTileType).isSelected,
    );

    this._isAllSelected = allSelected;
    this._SelectAllButton.patch({
      label: allSelected
        ? translate('myStuff.deselectAll')
        : translate('myStuff.selectAll'),
    });

    shouldActivateRemoveButton
      ? this.enableRemoveButton()
      : this.disableRemoveButton();
  }

  static override _states() {
    return [
      class EditButton extends this {
        override _getFocused() {
          return this._EditButton;
        }

        override _handleRight() {
          if (this._isEditing && this._EditActionsList.visible) {
            this._setState('EditActionsList');
          }
        }

        override _handleDown() {
          this._setState('ItemsList');
        }
      },
      class EditActionsList extends this {
        override _getFocused() {
          return this._EditActionsList;
        }

        override _handleLeft() {
          this._setState('EditButton');
        }

        override _handleDown() {
          this._setState('ItemsList');
        }
      },
      class ItemsList extends this {
        override _getFocused() {
          return this._ItemsList;
        }

        override _handleUp() {
          if (this._EditButton.visible) {
            this._setState('EditButton');
          } else {
            super._handleUp();
          }
        }

        override _handleBack() {
          this._ItemsList.items.length && this._ItemsList.index > 0
            ? this._ItemsList.setFirst()
            : super._handleBack();
        }
      },
      class EmptyGridSection extends this {
        override _getFocused() {
          return this._EmptySection.button;
        }
      },
    ];
  }

  disableRemoveButton() {
    const color = Colors('disabledMyStuffButton').get();
    const fontColor = Colors('disabledMyStuffButtonText').get();

    this.updateRemoveButton(color, fontColor, TRASH_ICONS.clicked);

    this._EditActionsList.patch({
      index: 0,
      unfocusableIndices: [1],
    });
  }

  enableRemoveButton() {
    const color = Colors('buttonInactive').get();
    const fontColor = Colors('text').get();

    this.updateRemoveButton(color, fontColor, TRASH_ICONS.unfocused);

    this._EditActionsList.patch({
      unfocusableIndices: [],
    });
  }

  showSelectionMode(visible: boolean) {
    this._ItemsList.items.forEach(item => {
      (item as SelectableTileType).showSelectionMode = visible;
    });
  }

  reportItemSelected(
    rowTitle: string,
    data?: Media,
    context: SelectItemContext<TileParentContext> = {},
  ) {
    const mainIndex = 0;
    const crossIndex = this._ItemsList.index;

    reportItemSelected(
      {
        imageUrl: context?.imageUrl,
        crossIndex,
        mainIndex,
        data,
        rowTitle,
        isTivoTileSource: false,
      },
      this._pageId,
    );
  }
}
